// Dialog isolation (v1.3 ticket 04, RCMC-23) and a named close button (RCMC-02) for every PrimeReact Dialog.
//
//   const isolation = useDialogIsolation(isOpen, "Add New Holding");
//   <Dialog header="Add New Holding" visible={isOpen} onHide={close} {...isolation}>
//
// While the dialog is open, #app-root (everything a dialog covers, see App.js) carries `inert`, so the page
// behind it leaves the accessibility tree and the Tab order. PrimeReact portals the dialog to <body> and the
// Announcer's live regions sit beside #app-root, so both stay live. On open, focus goes to the dialog's
// first field. PrimeReact already keeps Tab inside the dialog, closes it on Escape and returns focus to the
// opener on close.
//
// Timing matters in a real browser (jsdom does not enforce `inert`; seam 2 checks the real behaviour):
// - `inert` goes on in the Dialog's onShow, after the open transition. By then PrimeReact has recorded the
//   opener for focus return, and focus is moved into the dialog first. If the opener became inert while it
//   still had focus, Chrome would drop focus to <body>, and PrimeReact would later "return" focus there.
// - `inert` comes off as soon as the dialog is told to close, before PrimeReact's exit transition ends and
//   it focuses the opener again: an inert element cannot take focus.
import { useCallback, useEffect, useRef } from "react";
import { DomHandler } from "primereact/utils";

// One entry per open dialog, so closing one dialog never un-isolates the page under another
const openDialogs = new Set();

function syncAppRoot() {
  const root = document.getElementById("app-root");
  if (!root) return;
  // An attribute string, not a JSX prop: React 18 drops a boolean `inert` prop
  if (openDialogs.size > 0) root.setAttribute("inert", "");
  else root.removeAttribute("inert");
}

/**
 * @param {boolean} visible  the same value passed to the Dialog's `visible`
 * @param {string}  name     what the dialog is for; the close button is announced as "Close <name>"
 * @returns props to spread onto the PrimeReact Dialog: `ref`, `onShow`, `ariaCloseIconLabel`
 */
export function useDialogIsolation(visible, name) {
  const dialogRef = useRef(null);
  const token = useRef(Symbol(name)); // this dialog's entry in openDialogs
  const visibleRef = useRef(visible);
  visibleRef.current = visible;

  const release = useCallback(() => {
    openDialogs.delete(token.current);
    syncAppRoot();
  }, []);

  // Closing, or unmounting while open (e.g. a redirect), gives the page back at once
  useEffect(() => (visible ? release : undefined), [visible, release]);

  // PrimeReact calls onShow when the open transition ends, just before it would focus the close button
  const onShow = useCallback(() => {
    const dialog = dialogRef.current && dialogRef.current.getElement();
    if (!visibleRef.current || !dialog) return; // closed again before the transition finished
    // The first field; a dialog without one gets its first control (the close button, if it has one)
    const focusable = DomHandler.getFocusableElements(dialog);
    const target = focusable.find((el) => el.matches("input, select, textarea")) || focusable[0];
    if (target) target.focus();
    openDialogs.add(token.current);
    syncAppRoot();
  }, []);

  // PrimeReact 10.6.2 reads its close-button label from a locale key that does not exist, leaving the
  // button unnamed; ariaCloseIconLabel overrides it (pinned by the RCMC-02 tests)
  return { ref: dialogRef, onShow, ariaCloseIconLabel: `Close ${name}` };
}
